import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core'
import { Sort } from '@angular/material/sort'
import { Router, ActivatedRoute } from '@angular/router'
import { forkJoin, of, switchMap, Subject } from 'rxjs'
import { map, take, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ApplicationArea, CycleConfigDto, ReviewPlan, UserReview, UserReviewClient } from 'src/app/shared/services/ppv.api'
import { GlobalAdminService } from 'src/app/dvm-tool/globalAdmins.service'
import dayjs from 'dayjs'
import { ToastrService } from 'ngx-toastr'
import utc from 'dayjs/plugin/utc'
import tz from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(tz)

@Component({
   selector: 'dvm-tool-team-dashboard',
   templateUrl: './dvm-tool-team-dashboard.component.html',
   styleUrls: ['./dvm-tool-team-dashboard.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class DVMToolTeamDashboardComponent implements OnInit, OnDestroy {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   public isDVMToolAdmin = false
   public isDVM = false
   public isRM = false
   public isHM = false
   public isCOS = false
   public isCOSA = false
   public isOpsVP = false
   public isOpsDirector = false
   public userId: number = null
   public userName: string
   isGlobalAdmin = this.globalAdminService.isGlobalAdmin
   isReportingAdmin = this.globalAdminService.isReportingAdmin

   reviewType: string = 'TEAM'

   public userReviews: UserReview[] = []
   public reviewPlans: ReviewPlan[] = []

   public isUserReviewsLoaded = false
   public isPlansLoaded = false

   public allRows: ViewRow[] = []
   public dashboardRows: ViewRow[] = []

   public pagedRows: ViewRow[] = []
   public pageSize = 200
   public pageIndex = 0
   public totalPages = 1

   selectedPractice: string | null = null
   selectedStatus: string | null = null
   selectedReviewerName: string | null = null
   selectedRMName: string | null = null

   rtSelectedOptions: { label: string; value: string | null; disabled?: boolean }[] = [
      { label: 'Show All', value: null },
      { label: 'Clinical', value: 'CLINICAL' },
      { label: 'Cross-Trained', value: 'CROSSTRAINED' },
      { label: 'Reception', value: 'RECEPTION' },
      { label: 'Unset', value: '' },
   ]
   selectedRT: string[] = ['null']

   activeTeamSubTab: 'roster' | 'reviews' = 'reviews'

   selectedCycle = ''
   cycleConfigs: CycleConfigDto[] = []
   cycleConfigsLoaded = false
   tabCycles = ['Q1', 'Q2', 'Q3', 'Q4']

   public cycleOpen = true
   public cycleClosedReason = ''
   public readOnlyMode = false

   reviewerNameOptions: { label: string; value: string | null }[] = []
   practiceOptions: { label: string; value: string | null }[] = []
   statusOptions: { label: string; value: string | null }[] = [
      { label: 'Show All', value: null },
      { label: 'Completed', value: 'completed' },
      { label: 'In Review', value: 'in-review' },
      { label: 'In Progress', value: 'in-progress' },
      { label: 'Not Started', value: 'not-started' },
   ]

   public teamReviewOptions: Array<{ label: string; value: string | null }> = [
      { label: 'Clinical', value: 'CLINICAL' },
      { label: 'Cross-Trained', value: 'CROSSTRAINED' },
      { label: 'Reception', value: 'RECEPTION' },
   ]

   private adminUnreadCountMap = new Map<string, number>()

   private adminFollowUpMap = new Map<string, number>()

   showAllNames = false
   deferredFilterOptions: { label: string; value: string | null; disabled?: boolean }[] = [
      { label: 'Show All', value: null },
      { label: 'Active', value: 'active' },
      { label: 'Deferred', value: 'deferred' },
      { label: 'On Leave', value: 'LOAINACTIVE' },
      { label: 'More...', value: '__more__' },
   ]
   selectedDeferredFilters: string[] = ['null']
   searchName: string = ''

   private dvmNames: string[] = []
   rmOptions: { label: string; value: string | null }[] = []

   private isFetching = false

   private destroy$ = new Subject<void>()
   private searchInput$ = new Subject<string>()

   private practiceColorMap: Map<string, { bg: string; color: string; borderTopColor: string; borderBottomColor: string }> = new Map()

   private readonly practicePalette: { bg: string; color: string; borderTopColor: string; borderBottomColor: string }[] = [
      { bg: '#ffefcc', color: '#976a12', borderTopColor: '#976a1233', borderBottomColor: '#976a1233' },
      { bg: '#e6f1fa', color: '#306eba', borderTopColor: '#306eba33', borderBottomColor: '#306eba33' },
      { bg: '#fdeee6', color: '#f9573b', borderTopColor: '#f9573b33', borderBottomColor: '#f9573b33' },
      { bg: '#daf3f2', color: '#26b5ae', borderTopColor: '#26b5ae33', borderBottomColor: '#26b5ae33' },
      { bg: '#e9dee9', color: '#831183', borderTopColor: '#83118333', borderBottomColor: '#83118333' },
      { bg: '#fae8e8', color: '#d80c47', borderTopColor: '#d80c4733', borderBottomColor: '#d80c4733' },
      { bg: '#E1F5EE', color: '#0F6E56', borderTopColor: '#0F6E5633', borderBottomColor: '#0F6E5633' },
      { bg: '#FBEAF0', color: '#993556', borderTopColor: '#99355633', borderBottomColor: '#99355633' },
      { bg: '#eefae8', color: '#6a9d1b', borderTopColor: '#6a9d1b33', borderBottomColor: '#6a9d1b33' },
      { bg: '#f4f3eb', color: '#6e674c', borderTopColor: '#6e674c33', borderBottomColor: '#6e674c33' },
   ]

   trackByReviewee = (_: number, row: DashboardRow) => row.revieweeId

   private buildFilterOptions() {
      const byFilters = this.selectedDeferredFilters
      const byRT = this.selectedRT
      const byName = this.searchName?.toLowerCase()

      const matchesOtherFilters = (r: ViewRow, exclude: 'practice' | 'reviewer' | 'rm' | 'status') =>
         (byName ? r._nameLower?.includes(byName) : true) &&
         (byFilters.length === 0
            ? true
            : byFilters.some((f) =>
                 f === 'active'
                    ? !this.isDeferredRow(r) && r.revieweeUserStatus != 'LOAINACTIVE'
                    : f === 'deferred'
                      ? this.isDeferredRow(r)
                      : f === 'LOAINACTIVE'
                        ? r.revieweeUserStatus == 'LOAINACTIVE'
                        : true
              )) &&
         (byRT.length === 0
            ? true
            : byRT.some((f) =>
                 f === 'CLINICAL'
                    ? r.reviewType === 'CLINICAL'
                    : f === 'CROSSTRAINED'
                      ? r.reviewType === 'CROSSTRAINED'
                      : f === 'RECEPTION'
                        ? r.reviewType === 'RECEPTION'
                        : f === '' || f === 'Unset'
                          ? !r.reviewType
                          : true
              )) &&
         (exclude === 'reviewer' || !this.selectedReviewerName ? true : this.getReviewerName(r) === this.selectedReviewerName) &&
         (exclude === 'practice' || !this.selectedPractice ? true : r.practice === this.selectedPractice) &&
         (exclude === 'rm' || !this.selectedRMName ? true : r.rmName === this.selectedRMName) &&
         (exclude === 'status' || !this.selectedStatus ? true : this.getReviewStatus(r) === this.selectedStatus)

      const reviewers = new Set<string>()
      const practices = new Set<string>()
      const rms = new Set<string>()
      const statuses = new Set<string>()
      const names = new Set<string>()

      for (const r of this.allRows) {
         if (matchesOtherFilters(r, 'reviewer')) {
            const reviewer = this.getReviewerName(r)
            if (reviewer) reviewers.add(reviewer)
         }

         if (matchesOtherFilters(r, 'practice') && r.practice) practices.add(r.practice)
         if (matchesOtherFilters(r, 'rm') && r.rmName) rms.add(r.rmName)
         if (matchesOtherFilters(r, 'status')) statuses.add(this.getReviewStatus(r))
         if (r.name) names.add(r.name)
      }

      const toOptions = (s: Set<string>) =>
         Array.from(s)
            .sort((a, b) => a.localeCompare(b))
            .map((v) => ({ label: v, value: v }))

      this.reviewerNameOptions = toOptions(reviewers)
      this.practiceOptions = toOptions(practices)
      this.rmOptions = toOptions(rms)
      this.statusOptions = [
         { label: 'Show All', value: null },
         ...(statuses.has('completed') ? [{ label: 'Completed', value: 'completed' }] : []),
         ...(statuses.has('in-review') ? [{ label: 'In Review', value: 'in-review' }] : []),
         ...(statuses.has('in-progress') ? [{ label: 'In Progress', value: 'in-progress' }] : []),
         ...(statuses.has('not-started') ? [{ label: 'Not Started', value: 'not-started' }] : []),
      ]
      this.dvmNames = Array.from(names).sort((a, b) => a.localeCompare(b))

      this.rebuildDeferredOptions()
      this.rebuildRTOptions()
   }

   rebuildDeferredOptions() {
      const base = [
         { label: 'Show All', value: null },
         { label: 'Active', value: 'active' },
         { label: 'Deferred', value: 'deferred' },
         { label: 'On Leave', value: 'LOAINACTIVE' },
      ]

      if (this.showAllNames) {
         const capped = this.dvmNames.slice(0, 200)
         this.deferredFilterOptions = [...base, { label: 'Hide names', value: '__less__' }, ...capped.map((v) => ({ label: v, value: `name:${v}` }))]
      } else {
         this.deferredFilterOptions = [...base, { label: 'More...', value: '__more__' }]
      }
   }

   rebuildRTOptions() {
      const base = [
         { label: 'Show All', value: null },
         { label: 'Clinical', value: 'CLINICAL' },
         { label: 'Cross-Trained', value: 'CROSSTRAINED' },
         { label: 'Reception', value: 'RECEPTION' },
         { label: 'Unset', value: '' },
      ]

      if (this.showAllNames) {
         const capped = this.dvmNames.slice(0, 200)
         this.rtSelectedOptions = [...base, { label: 'Hide names', value: '__less__' }, ...capped.map((v) => ({ label: v, value: `name:${v}` }))]
      } else {
         this.rtSelectedOptions = [...base, { label: 'More...', value: '__more__' }]
      }
   }

   onNameSearch(val: string): void {
      this.searchInput$.next(val ?? '')
   }

   private currentSort: Sort = { active: '', direction: '' }

   constructor(
      private authMgr: AuthManager,
      private router: Router,
      private route: ActivatedRoute,
      private toastr: ToastrService,
      private userReviewClient: UserReviewClient,
      public globalAdminService: GlobalAdminService
   ) {
      this.searchInput$.pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((val) => {
         this.searchName = val
         this.pageIndex = 0
         this.applyFiltersSort()
      })
   }

   ngOnInit(): void {
      // Let the landing-page search deep-link a name into the roster
      const q = this.route.snapshot.queryParamMap.get('q')
      if (q) this.searchName = q

      this.authMgr
         .getCurrentUser()
         .pipe(take(1), takeUntil(this.destroy$))
         .subscribe((user) => {
            this.applyUserFlags(user)

            if (!this.isDVMToolAdmin) {
               this.userReviewClient
                  .getReviewType(this.userId.toString())
                  .pipe(takeUntil(this.destroy$))
                  .subscribe({
                     next: (reviewType) => {
                        this.router.navigate(['/performance-hub/team-reviews', this.userId], {
                           queryParams: { rt: reviewType },
                           queryParamsHandling: 'merge',
                        })
                        return
                     },
                  })
               return
            }

            // Load cycle config first, then load table data
            this.loadCycleConfig(() => this.loadDashboard(this.selectedCycle))
         })
   }

   ngOnDestroy(): void {
      this.destroy$.next()
      this.destroy$.complete()
   }

   private applyUserFlags(user: any): void {
      this.userId = user.id
      this.userName = user.displayName

      this.isDVMToolAdmin = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['DVMToolAdmin'],
      })

      const cDVM = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['DVMTool'],
      })

      const cRM = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['RM'],
      })

      const cHM = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['HospitalManager'],
      })

      const cOpsVP = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['OpsVP'],
      })

      const cOpsDirector = this.authMgr.canAccess(user, {
         application: ApplicationArea.DVMTool,
         roleName: ['OpsDirector'],
      })

      this.isRM = user.dayforceJobCode === 'OPS-RM'
      this.isHM = user.dayforceJobCode === 'HM' || user.dayforceJobCode === 'AREA_MANAGER'
      this.isCOSA = user.dayforceJobCode === 'COSA'
      this.isCOS = user.dayforceJobCode === 'VET-COS'
      this.isOpsVP = user.dayforceJobCode === 'OPS-SVP' || user.dayforceJobCode === 'OPS-VP' || user.dayforceJobCode === 'CORP-VP' || cOpsVP
      this.isOpsDirector = user.dayforceJobCode === 'CORP-DIR' || cOpsDirector
      this.isDVM = user.dayforceJobCode === 'VET' || cDVM
   }

   loadCycleConfig(onLoaded?: () => void): void {
      this.userReviewClient
         .getCycleConfig()
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: (configs) => {
               this.cycleConfigs = configs ?? []
               this.cycleConfigsLoaded = true
               this.refreshCycleState()
               onLoaded?.()
            },
            error: () => {
               this.cycleConfigs = []
               this.cycleConfigsLoaded = true
               this.refreshCycleState()
               onLoaded?.()
            },
         })
   }

   private refreshCycleState(): void {
      this.cycleOpen = this.isCycleOpen('CLINICAL', undefined)
      this.cycleClosedReason = this.getCycleClosedReason('CLINICAL')
      //this.readOnlyMode = !this.cycleOpen && !this.isReportingAdmin()
   }

   private getCycleConfig(reviewType: string, quarter?: string): CycleConfigDto | null {
      return this.cycleConfigs.find((c) => c.key === reviewType) ?? null
   }

   isCycleOpen(reviewType: string, quarter?: string): boolean {
      if (!this.cycleConfigsLoaded) return true

      const config = this.getCycleConfig('CLINICAL', quarter)

      if (!config) return false

      if (!config.isEnabled) return false

      const now = dayjs()
      if (config.openDate) {
         const open = dayjs(config.openDate).startOf('day')
         if (now.isBefore(open)) return false
      }
      if (config.closeDate) {
         const close = dayjs(config.closeDate).endOf('day')
         if (now.isAfter(close)) return false
      }

      return true
   }

   isCycleEnabled(reviewType: string, quarter?: string): boolean {
      const config = this.getCycleConfig('CLINICAL', quarter)
      if (!config || !config.isEnabled) return false
      return true
   }

   getCycleClosedReason(reviewType: string, quarter?: string): string {
      if (!this.cycleConfigsLoaded) return ''

      const config = this.getCycleConfig(reviewType, quarter)

      if (!config)
         return 'This review cycle has been disabled or is outside its active date window. Reviews cannot be opened until the cycle is re-enabled.'

      if (!config.isEnabled)
         return 'This review cycle has been disabled or is outside its active date window. Reviews cannot be opened until the cycle is re-enabled.'

      const now = dayjs()
      if (config.openDate && now.isBefore(dayjs(config.openDate).startOf('day'))) {
         return `This review cycle opens on ${dayjs(config.openDate).format('MMM D, YYYY')}.`
      }
      if (config.closeDate && now.isAfter(dayjs(config.closeDate).endOf('day'))) {
         return `This review cycle closed on ${dayjs(config.closeDate).format('MMM D, YYYY')}. Reviews cannot be opened until the cycle is re-enabled.`
      }

      return 'This cycle is currently unavailable.'
   }

   get isCurrentCycleOpen(): boolean {
      return this.cycleOpen
   }

   get currentCycleClosedReason(): string {
      return this.cycleClosedReason
   }

   setTeamSubTab(tab: 'roster' | 'reviews'): void {
      if (this.activeTeamSubTab === tab) return
      this.activeTeamSubTab = tab
      this.pageIndex = 0
      this.repage()
   }

   getPracticeColor(practiceName: string): { bg: string; color: string; borderTopColor: string; borderBottomColor: string } {
      if (!this.practiceColorMap.has(practiceName)) {
         const nextColor = this.practicePalette[this.practiceColorMap.size % this.practicePalette.length]
         this.practiceColorMap.set(practiceName, nextColor)
      }
      return this.practiceColorMap.get(practiceName)!
   }

   getInitials(name: string): string {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase()
   }

   private decorate(rows: DashboardRow[]): ViewRow[] {
      this.practiceColorMap.clear()
      const out: ViewRow[] = []
      for (const r of rows) {
         const v = r as ViewRow
         const deferred = !!r.isDeferred && r.deferralStatus != 'Pending' && r.deferralStatus != 'Rejected' && r.deferralStatus != 'ReEnabled'
         const id = r.revieweeId?.toString()

         v._color = this.getPracticeColor(r.practice)
         v._initials = this.getInitials(r.name)
         v._deferred = deferred
         v._onLeave = r.revieweeUserStatus === 'LOAINACTIVE'
         v._isHome = this.userName === r.hmName
         v._unread = this.adminFollowUpMap.has(id) ? (this.adminUnreadCountMap.get(id) ?? 0) : 0
         v._status = this.getReviewStatus(r)
         v._payClassLabel = r.payClass === 'FT' ? 'Full-time' : r.payClass === 'PT' ? 'Part-time' : r.payClass
         v._nameLower = (r.name ?? '').toLowerCase()
         v._reviewerName = this.getReviewerName(r)
         v._newReviewerName = (r as any)['new' + this.getReviewerLabel(true)] ?? ''
         v._reviewTypeLabel =
            r.reviewType === 'CLINICAL' ? 'Clinical' : r.reviewType === 'CROSSTRAINED' ? 'Cross-Trained' : r.reviewType ? 'Reception' : ''
         v._dvmIncomplete = this.isIncompleteSubmitted(r.isDVMSubmitted, r.dvmSubmittedOn)
         v._cosIncomplete = this.isIncompleteSubmitted(r.isCOSSubmitted, r.cosSubmittedOn)

         out.push(v)
      }
      return out
   }

   private hashName(name: string): number {
      if (!name) return 0
      return name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
   }

   onReviewTypeChange(selectedValue: string, row: any, modals: any): void {
      this.userReviewClient
         .updateCycle(
            {
               revieweeUserId: row.revieweeId,
               reviewerUserId: row.hmId || this.userId,
               cycle: '',
               practiceSKey: row.practiceSKey,
               practiceName: row.practice,
               revieweeName: row.name,
               cosName: row.cosName,
               rmName: row.rmName,
               hmName: row.hmName,
               vetLeaderName: row.vetLeaderName,
               directorName: row.directorName,
               vpName: row.vpName,
               reviewType: selectedValue,
               hireDate: row.hireDate,
            },
            true,
            false
         )
         .subscribe({
            next: () => {
               row._reviewTypeLabel =
                  selectedValue === 'CLINICAL' ? 'Clinical' : selectedValue === 'CROSSTRAINED' ? 'Cross-Trained' : selectedValue ? 'Reception' : ''
               this.toastr.success('Review type saved')
               if (selectedValue === 'CROSSTRAINED') {
                  modals.openJobDescDialog(row)
               }
            },
            error: () => {},
         })
   }

   onRowClick(row: DashboardRow): void {
      //if (row.revieweeUserStatus == 'LOAINACTIVE' && !this.isReportingAdmin()) return

      // Block navigation if the cycle is closed
      //if (!this.cycleOpen && !this.isReportingAdmin()) return

      if (!row.reviewType) {
         this.toastr.info('The review type has not yet been set for this user.')
         return
      }

      if (row) {
         const localDate = dayjs.utc(row.lastUpdated).local()
         const formattedDate = localDate.format('MM/DD/YYYY')
         const formattedTime = localDate.format('hh:mm A')
         const lastUpdated = formattedDate + ' ' + formattedTime
         this.router.navigate(['/performance-hub/team-reviews', row.revieweeId], {
            queryParams: {
               rt: row.reviewType ? row.reviewType : this.reviewType,
               from: this.reviewType,
            },
            state: {
               lastUpdated: lastUpdated,
            },
         })
      }
   }

   getRevieweeLabel(short: boolean = false): string {
      return short ? 'TEAM' : 'Team Member'
   }

   getReviewerLabel(short: boolean = false): string {
      switch (this.reviewType) {
         case 'CROSSTRAINED':
            return short ? 'HM' : 'Hospital Manager'
         case 'RECEPTION':
            return short ? 'HM' : 'Hospital Manager'
         case 'CLINICAL':
            return short ? 'HM' : 'Hospital Manager'
         case 'OPEN':
            return short ? 'HM' : 'Hospital Manager'
         case 'TEAM':
         default:
            return short ? 'HM' : 'Hospital Manager'
      }
   }

   getReviewerName(userReview: any): string {
      switch (this.reviewType) {
         case 'CROSSTRAINED':
            return userReview.hmName
         case 'RECEPTION':
            return userReview.hmName
         case 'CLINICAL':
            return userReview.hmName
         case 'OPEN':
            return userReview.hmName
         case 'TEAM':
         default:
            return userReview.hmName
      }
   }

   private loadDashboard(cycle?: string): void {
      if (this.isFetching) return
      this.isFetching = true
      this.isUserReviewsLoaded = false
      this.isPlansLoaded = false

      const reviews$ =
         this.reviewType === 'TEAM'
            ? forkJoin({
                 clinical: this.userReviewClient.getUserReviewsAll('CLINICAL', cycle),
                 crossTrained: this.userReviewClient.getUserReviewsAll('CROSSTRAINED', cycle),
                 reception: this.userReviewClient.getUserReviewsAll('RECEPTION', cycle),
              }).pipe(map(({ clinical, crossTrained, reception }) => [...clinical, ...crossTrained, ...reception]))
            : this.userReviewClient.getUserReviewsAll(this.reviewType, cycle)

      forkJoin({
         reviews: reviews$,
         plans: this.userReviewClient.getUsersAll(this.reviewType, cycle),
      })
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: ({ reviews, plans }) => {
               this.userReviews = reviews as UserReview[]
               this.reviewPlans = plans
               this.isUserReviewsLoaded = true
               this.isPlansLoaded = true
               this.isFetching = false

               this.updateDashboardRows()
               this.buildFilterOptions()
               this.applyFiltersSort()

               this.loadAdminFollowUpFlags()
            },
            error: (error) => {
               console.error('Error fetching data:', error)
               this.isUserReviewsLoaded = true
               this.isPlansLoaded = true
               this.isFetching = false
            },
         })
   }

   // Kept for API compatibility with anything that calls these (modal).
   // Both now trigger the same single coordinated reload.
   public getUserReviewsAll(cycle?: string) {
      this.loadDashboard(cycle ?? this.selectedCycle)
   }

   public getUsersAll(cycle?: string) {
      this.loadDashboard(cycle ?? this.selectedCycle)
   }

   private loadAdminFollowUpFlags(): void {
      this.userReviewClient
         .getFollowUpSummary()
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: (summary) => {
               this.adminFollowUpMap.clear()
               this.adminUnreadCountMap.clear()

               for (const s of summary ?? []) {
                  const id = s.revieweeId?.toString()
                  if (!id) continue
                  this.adminFollowUpMap.set(id, 1)
                  this.adminUnreadCountMap.set(id, s.unreadCount ?? 0)
               }

               for (const row of this.allRows) {
                  const id = row.revieweeId?.toString()
                  row.hasAdminFollowUp = this.adminFollowUpMap.has(id)
                  row._unread = this.adminUnreadCountMap.get(id) ?? 0
               }
            },
            error: () => {
               /* non-critical, follow-up button just won't show */
            },
         })
   }

   updateDashboardRows() {
      const planMap = new Map<string, ReviewPlan>()
      this.reviewPlans.forEach((plan) => {
         planMap.set(plan.revieweeUserId?.toString(), plan)
      })

      const sortedReviews = this.userReviews
         .map((ur) => ({ ur, ts: ur.dateUpdated ? Date.parse(ur.dateUpdated as any) || 0 : 0 }))
         .sort((a, b) => b.ts - a.ts)
         .map((x) => x.ur)

      const dashboardRowsFromExisting = sortedReviews.map((plan) => {
         const reviewPlan = planMap.get(plan.revieweeId?.toString())

         const upd = plan.dateUpdated ? dayjs.utc(plan.dateUpdated).local() : null
         const updDate = upd ? upd.format('MM/DD/YYYY') : ''
         const updTime = upd ? upd.format('hh:mm A') : ''
         const updFull = upd ? updDate + ' ' + updTime : ''

         return {
            name: reviewPlan?.revieweeDisplayName || plan.revieweeName || '',
            practice: reviewPlan?.practiceName || plan.practiceName || '',
            revieweeUserStatus: reviewPlan?.revieweeUserStatus,
            rmName: reviewPlan?.rmDisplayName || plan.rmName || '',
            hmName: reviewPlan?.hmDisplayName || plan.hmName || '',
            hmName2: reviewPlan?.hmDisplayName2 || plan.hmName2 || '',
            hmId: reviewPlan?.hmUserId || plan.hmId || '',
            hmEmail: reviewPlan?.hmEmail || '',
            vetLeaderName: reviewPlan?.vetLeaderDisplayName || plan.vetLeaderName || '',
            cosName: reviewPlan?.cosDisplayName || plan.cosName || '',
            directorName: reviewPlan?.directorDisplayName || plan.directorName || '',
            vpName: reviewPlan?.vpDisplayName || plan.vpName || '',
            lastUpdated: updFull,
            lastUpdatedDisplay: updDate,
            hireDate: reviewPlan?.hireDate || plan.dateHired ? dayjs(reviewPlan?.hireDate || plan.dateHired).format('MM/DD/YYYY') : '',
            reviewUpdatedDate: updFull,
            revieweeId: plan.revieweeId,
            isCompleted: (plan as any).isCompleted ?? null,
            isDVMSubmitted: (plan as any).isDVMSubmitted ?? null,
            isCOSSubmitted: (plan as any).isCOSSubmitted ?? null,
            dvmSubmittedOn: (plan as any).dvmSubmittedOn
               ? dayjs
                    .utc((plan as any).dvmSubmittedOn)
                    .local()
                    .toDate()
                    .toLocaleDateString()
               : '',
            cosSubmittedOn: (plan as any).cosSubmittedOn
               ? dayjs
                    .utc((plan as any).cosSubmittedOn)
                    .local()
                    .toDate()
                    .toLocaleDateString()
               : '',
            completedOn: (plan as any).completedOn
               ? dayjs
                    .utc((plan as any).completedOn)
                    .local()
                    .toDate()
                    .toLocaleDateString()
               : '',
            isDeferred: (plan.isDeferred || reviewPlan?.isDeferred) ?? false,
            deferReason: (plan.deferReason || reviewPlan?.deferReason) ?? null,
            deferralStatus: (plan.deferralStatus || reviewPlan?.deferralStatus) ?? null,
            practiceSKey: reviewPlan?.practiceSKey || plan.practiceSKey || 0,
            reviewType: plan.reviewType || '',
            jobTitle: reviewPlan?.jobTitle || '',
            jobTitleCode: reviewPlan?.jobTitleCode || '',
            payClass: reviewPlan?.payClass || '',
            additionalResponsibilitiesCount:
               (reviewPlan as any)?.additionalResponsibilitiesCount ?? (plan as any)?.additionalResponsibilitiesCount ?? 0,
            newCOS: reviewPlan?.cosDisplayName == plan?.cosName ? '' : plan?.cosName,
            newRM: reviewPlan?.rmDisplayName == plan?.rmName ? '' : plan?.rmName,
            newCOSA: reviewPlan?.vetLeaderDisplayName == plan?.vetLeaderName ? '' : plan?.vetLeaderName,
            newDO: reviewPlan?.directorDisplayName == plan?.directorName ? '' : plan?.directorName,
            newVP: reviewPlan?.vpDisplayName == plan?.vpName ? '' : plan?.vpName,
            newHM: reviewPlan?.hmDisplayName == plan?.hmName ? '' : plan?.hmName,
            hasStarted: (plan as any).hasStarted ?? null,
            hasAttachment: reviewPlan?.hasAttachment ?? null,
         }
      })

      const reviewedIds = new Set<string>(this.userReviews.map((ur) => ur.revieweeId?.toString()))
      const unmatchedDvms: ReviewPlan[] = this.reviewPlans.filter((rp) => !reviewedIds.has(rp.revieweeUserId?.toString()))

      const dashboardRowsForNew: DashboardRow[] = unmatchedDvms.map((reviewPlan) => ({
         name: reviewPlan.revieweeDisplayName || '',
         practice: reviewPlan.practiceName || '',
         revieweeUserStatus: reviewPlan.revieweeUserStatus || '',
         lastUpdated: 'INCOMPLETE',
         lastUpdatedDisplay: 'INCOMPLETE',
         hireDate: reviewPlan.hireDate ? dayjs(reviewPlan.hireDate).format('MM/DD/YYYY') : '',
         rmName: reviewPlan.rmDisplayName || '',
         hmName: reviewPlan.hmDisplayName || '',
         hmName2: reviewPlan.hmDisplayName2 || '',
         hmId: reviewPlan.hmUserId || '',
         hmEmail: reviewPlan.hmEmail || '',
         vetLeaderName: reviewPlan.vetLeaderDisplayName || '',
         cosName: reviewPlan.cosDisplayName || '',
         directorName: reviewPlan.directorDisplayName || '',
         vpName: reviewPlan.vpDisplayName || '',
         revieweeId: reviewPlan.revieweeUserId,
         isCompleted: false,
         isDVMSubmitted: false,
         isCOSSubmitted: false,
         dvmSubmittedOn: '',
         cosSubmittedOn: '',
         completedOn: '',
         isDeferred: reviewPlan.isDeferred,
         deferReason: reviewPlan.deferReason,
         deferralStatus: reviewPlan.deferralStatus,
         practiceSKey: reviewPlan.practiceSKey || 0,
         reviewType: '',
         jobTitle: reviewPlan.jobTitle || '',
         jobTitleCode: reviewPlan.jobTitleCode || '',
         payClass: reviewPlan.payClass || '',
         additionalResponsibilitiesCount: (reviewPlan as any)?.additionalResponsibilitiesCount ?? 0,
         hasStarted: false,
         hasAttachment: false,
      }))

      const combinedDashboards = [...dashboardRowsForNew, ...dashboardRowsFromExisting]
      const uniqueRowsMap = new Map<string, DashboardRow>()
      combinedDashboards.forEach((row) => {
         const key = row.revieweeId?.toString()
         if (!uniqueRowsMap.has(key)) uniqueRowsMap.set(key, row)
      })

      let uniqueDashboardRowsFromExisting = Array.from(uniqueRowsMap.values())

      //if (!this.isHM) {
      uniqueDashboardRowsFromExisting = uniqueDashboardRowsFromExisting.sort((a, b) => {
         const aIsHomeHospital = this.userName === a.hmName
         const bIsHomeHospital = this.userName === b.hmName

         // Home-hospital rows first
         if (aIsHomeHospital !== bIsHomeHospital) {
            return aIsHomeHospital ? -1 : 1
         }

         // Then practice name
         const practiceCompare = (a.practice || '').localeCompare(b.practice || '')
         if (practiceCompare !== 0) {
            return practiceCompare
         }

         // Then team-member name
         return (a.name || '').localeCompare(b.name || '')
      })
      //}

      const filteredRows =
         this.reviewType === 'RM' && this.isRM
            ? uniqueDashboardRowsFromExisting.filter((row) => row.revieweeId?.toString() === this.userId?.toString())
            : uniqueDashboardRowsFromExisting

      this.allRows = this.decorate(filteredRows)
      this.dashboardRows = this.allRows
      this.repage()
   }

   isIncompleteSubmitted(isSubmitted: boolean | null | undefined, submittedOn: any): boolean {
      if (isSubmitted === false) return true
      if (submittedOn == null) return true
      if (typeof submittedOn === 'string' && submittedOn.trim().toLowerCase() === 'null') return true
      if (typeof submittedOn === 'string' && submittedOn.trim() === '') return true
      return !this.isValidDate(submittedOn)
   }

   isValidDate(value: string): boolean {
      const date = new Date(value)
      return !isNaN(date.getTime())
   }

   onPracticeChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedPractice = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   onReviewerChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedReviewerName = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   onRMChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedRMName = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   onStatusChange(val: any) {
      const actualVal = typeof val === 'string' ? val : ((val as { value?: string | null })?.value ?? null)
      this.selectedStatus = actualVal
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   applyFiltersSort() {
      const byFilters = this.selectedDeferredFilters
      const byRT = this.selectedRT
      const byName = this.searchName?.toLowerCase()

      const filtered = this.allRows.filter(
         (r) =>
            (byName ? r._nameLower?.includes(byName) : true) &&
            (byFilters.length === 0
               ? true
               : byFilters.some((f) =>
                    f === 'active'
                       ? !this.isDeferredRow(r) && r.revieweeUserStatus != 'LOAINACTIVE'
                       : f === 'deferred'
                         ? this.isDeferredRow(r)
                         : f === 'LOAINACTIVE'
                           ? r.revieweeUserStatus == 'LOAINACTIVE'
                           : true
                 )) &&
            (byRT.length === 0
               ? true
               : byRT.some((f) =>
                    f === 'CLINICAL'
                       ? r.reviewType === 'CLINICAL'
                       : f === 'CROSSTRAINED'
                         ? r.reviewType === 'CROSSTRAINED'
                         : f === 'RECEPTION'
                           ? r.reviewType === 'RECEPTION'
                           : f === '' || f === 'Unset'
                             ? !r.reviewType
                             : true
                 )) &&
            (this.selectedReviewerName ? this.getReviewerName(r) === this.selectedReviewerName : true) &&
            (this.selectedPractice ? r.practice === this.selectedPractice : true) &&
            (this.selectedRMName ? r.rmName === this.selectedRMName : true) &&
            (this.selectedStatus ? this.getReviewStatus(r) === this.selectedStatus : true)
      )

      this.dashboardRows = this.decorate(this.sortArray(filtered, this.currentSort))
      this.buildFilterOptions()
      this.repage()
   }

   private repage(): void {
      this.totalPages = Math.max(1, Math.ceil(this.dashboardRows.length / this.pageSize))
      if (this.pageIndex > this.totalPages - 1) this.pageIndex = this.totalPages - 1
      if (this.pageIndex < 0) this.pageIndex = 0
      const start = this.pageIndex * this.pageSize
      this.pagedRows = this.dashboardRows.slice(start, start + this.pageSize)
   }

   goToPage(i: number): void {
      const next = Math.max(0, Math.min(i, this.totalPages - 1))
      if (next === this.pageIndex) return
      this.pageIndex = next
      this.repage()
      document.querySelector('.table-container')?.scrollTo({ top: 0 })
   }

   get pageRangeStart(): number {
      return this.dashboardRows.length === 0 ? 0 : this.pageIndex * this.pageSize + 1
   }

   get pageRangeEnd(): number {
      return Math.min((this.pageIndex + 1) * this.pageSize, this.dashboardRows.length)
   }

   getReviewStatus(row: DashboardRow): string {
      if (row.isCompleted) return 'completed'
      if (row.dvmSubmittedOn && row.cosSubmittedOn) return 'in-review'
      if (!row.dvmSubmittedOn && !row.cosSubmittedOn && !row.hasStarted) return 'not-started'
      return 'in-progress'
   }

   sortData(sort: Sort) {
      this.currentSort = sort
      this.dashboardRows = this.decorate(this.sortArray(this.dashboardRows, sort))
      this.pageIndex = 0
      this.repage()
   }

   private sortArray(rows: DashboardRow[], sort: Sort): DashboardRow[] {
      if (!sort?.active || !sort.direction) {
         return rows.slice()
      }

      const isAsc = sort.direction === 'asc'

      const getVal = (r: DashboardRow): string | number | null => {
         switch (sort.active) {
            case 'name':
               return r.name?.toLowerCase() ?? ''

            case 'cosName':
               return r.cosName?.toLowerCase() ?? ''

            case 'practice':
               return r.practice?.toLowerCase() ?? ''

            case 'hmName':
               return r.hmName?.toLowerCase() ?? ''

            case 'jobTitleCode':
               return r.jobTitleCode?.toLowerCase() ?? ''

            case 'jobTitle':
               return r.jobTitle?.toLowerCase() ?? ''

            case 'payClass':
               return r.payClass?.toLowerCase() ?? ''

            case 'reviewType':
               return r.reviewType?.toLowerCase() ?? ''

            case 'lastUpdated':
               return this.parseDate(r.lastUpdatedDisplay)

            case 'dvmSubmittedOn':
               return this.parseDate(r.dvmSubmittedOn)

            case 'cosSubmittedOn':
               return this.parseDate(r.cosSubmittedOn)

            case 'hireDate':
               return this.parseDate(r.hireDate)

            case 'status':
               return this.getReviewStatus(r)?.toLowerCase() ?? ''

            default:
               return ''
         }
      }

      return rows
         .map((r) => ({
            r,
            v: getVal(r),
         }))
         .sort((a, b) => {
            const av = a.v
            const bv = b.v

            if (av == null && bv == null) return 0
            if (av == null) return isAsc ? -1 : 1
            if (bv == null) return isAsc ? 1 : -1

            if (av < bv) return isAsc ? -1 : 1
            if (av > bv) return isAsc ? 1 : -1

            return 0
         })
         .map((x) => x.r)
   }

   private parseDate(value: string | Date | null | undefined): number | null {
      if (!value) return null

      if (value instanceof Date) {
         return value.getTime()
      }

      const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)

      if (match) {
         const [, month, day, year] = match

         return new Date(Number(year), Number(month) - 1, Number(day)).getTime()
      }

      const timestamp = Date.parse(value)

      return Number.isNaN(timestamp) ? null : timestamp
   }

   onCycleChange(val: any): void {
      this.selectedCycle = val
      this.pageIndex = 0
      this.loadDashboard(val)
   }

   isDeferredRow(row: DashboardRow): boolean {
      return !!row.isDeferred && row.deferralStatus != 'Pending' && row.deferralStatus != 'Rejected' && row.deferralStatus != 'ReEnabled'
   }

   adminReEnableUser(row: DashboardRow): void {
      this.userReviewClient.reEnableDeferredUser(row.revieweeId.toString()).subscribe({
         next: () => {
            row.isDeferred = false
            row.deferReason = ''
            this.applyFiltersSort()
            this.toastr.success('User has been re-enabled.')
         },
         error: () => console.error('Failed to re-enable user'),
      })
   }

   onDeferredFilterChange(val: any): void {
      return
   }

   onDeferredChipChange(event: any): void {
      this.selectedDeferredFilters = event.value ?? []
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   onRTChange(event: any): void {
      this.selectedRT = event.value ?? []
      this.pageIndex = 0
      this.applyFiltersSort()
   }

   // NOTE: kept for compatibility, but the template now reads row._unread directly.
   getUserUnreadFollowUpCount(row: DashboardRow): number {
      const id = row.revieweeId?.toString()
      if (!this.adminFollowUpMap.has(id)) return 0
      return this.adminUnreadCountMap.get(id) ?? 0
   }

   onNavigateToUserManagement(row: any): void {
      this.router.navigate(['/performance-hub/admin', 'user-management'], {
         state: { pendingUserSelect: row },
      })
   }
}

export interface FullReviewPreviewQuestion {
   label: string
   required: boolean
   type: string
   options: string[]
   columns: string[]
   showMarkNotApplicable?: boolean
}

export interface FullReviewPreviewSection {
   title: string
   questions: FullReviewPreviewQuestion[]
}

export interface AdditionalResponsibilityRow {
   id?: number
   revieweeUserId?: string | number
   reviewType?: string
   responsibilityText: string
   displayOrder?: number
   assignedRevieweeIds?: number[]
   activeTab?: 'write' | 'preview'
   dupMatch?: string | null
}

export interface DashboardRow {
   name: string
   practice: string
   lastUpdated: string
   lastUpdatedDisplay?: string
   hireDate: string
   reviewUpdatedDate?: string
   rmName: string
   hmName: string
   hmEmail: string
   hmId: string
   hmName2: string
   vetLeaderName: string
   cosName: string
   directorName: string
   vpName: string
   revieweeId: string
   revieweeUserStatus: string
   isDVMSubmitted?: boolean | null
   isCOSSubmitted?: boolean | null
   dvmSubmittedOn?: string
   cosSubmittedOn?: string
   isCompleted?: boolean | null
   completedOn?: string
   isDeferred?: boolean
   deferReason?: string
   deferralStatus?: string
   practiceSKey?: number
   hasPendingDeferralRequest?: boolean
   hasAdminFollowUp?: boolean
   reviewType?: string
   newRM?: string
   newCOS?: string
   newCOSA?: string
   newDO?: string
   newVP?: string
   newHM?: string
   jobTitle?: string
   jobTitleCode?: string
   payClass?: string
   additionalResponsibilitiesCount?: number
   includeCareerExploration?: boolean
   hasStarted?: boolean
   hasAttachment?: boolean
}
export interface ViewRow extends DashboardRow {
   _color?: { bg: string; color: string; borderTopColor: string; borderBottomColor: string }
   _initials?: string
   _deferred?: boolean
   _onLeave?: boolean
   _isHome?: boolean
   _unread?: number
   _status?: string
   _payClassLabel?: string
   _nameLower?: string
   _reviewerName?: string
   _newReviewerName?: string
   _reviewTypeLabel?: string
   _dvmIncomplete?: boolean
   _cosIncomplete?: boolean
   _editingType?: boolean
}
